import { useMemo, useState } from 'react';
import { AlertTriangle, Heart, Mail, Printer } from 'lucide-react';
import Seo from '../components/Seo';

const sections = [
  {
    title: 'Foundations of Emotional Safety',
    questions: [
      'I feel respected and valued by the people in my life.',
      'I can share my feelings and thoughts without constantly fearing judgment or ridicule.',
      'When I speak up, people usually listen even when they do not agree.',
      'When I make mistakes, I am more likely to be met with understanding than harsh punishment.',
      'Feedback in my life is usually given with care and respect.',
    ],
  },
  {
    title: 'Trust & Transparency',
    questions: [
      'I have people I can be honest and vulnerable with, and I trust them to handle that with care.',
      'Important information that affects me is usually shared honestly and in a timely way.',
      'My emotional, physical, time and privacy boundaries are generally respected.',
      'Conflict is usually handled calmly, fairly and respectfully.',
    ],
  },
  {
    title: 'Inclusion & Belonging',
    questions: [
      'I feel that I belong somewhere I can be my real self.',
      'My differences are more often welcomed than dismissed or mocked.',
      'People in my life try to understand how I feel and show that they care.',
    ],
  },
  {
    title: 'Support & Care',
    questions: [
      'When I am struggling emotionally, I know at least one person or place I can contact.',
      'When I am burned out or overwhelmed, it tends to be noticed instead of ignored.',
      'Kindness, encouragement and supportive gestures show up in my life regularly.',
    ],
  },
  {
    title: 'Leadership & Power Dynamics',
    questions: [
      'People with authority in my life usually model honesty, empathy and accountability.',
      'Problems involving people in authority are usually addressed fairly and without abuse.',
      'People with power over me are, overall, approachable and somewhat trustworthy.',
    ],
  },
];

const totalQuestions = sections.reduce((sum, section) => sum + section.questions.length, 0);

function resultBand(score) {
  if (score >= 4.5) return ['Excellent', 'Emotional safety appears strong in many areas of your life.'];
  if (score >= 3.5) return ['Good', 'There is a healthy base with some rough spots worth noticing.'];
  if (score >= 2.5) return ['Caution', 'Emotional safety may depend heavily on the situation or people involved.'];
  return ['Critical', 'Some environments or relationships may feel emotionally unsafe. Consider reaching out for support.'];
}

export default function MentalHealth() {
  const [answers, setAnswers] = useState({});
  const answeredCount = Object.keys(answers).length;
  const score = useMemo(() => answeredCount ? Object.values(answers).reduce((sum, value) => sum + value, 0) / answeredCount : 0, [answers, answeredCount]);
  const complete = answeredCount === totalQuestions;
  const [label, summary] = resultBand(score);

  function emailResults() {
    const body = complete
      ? `My Emotional Safety Audit score was ${score.toFixed(1)} / 5 (${label}).\n\nI understand this is not a diagnosis or emergency service.`
      : `I started the Emotional Safety Audit and answered ${answeredCount} of ${totalQuestions} questions.`;
    window.location.href = `mailto:wecare@jonnystowingbc.ca?subject=Emotional%20Safety%20Audit&body=${encodeURIComponent(body)}`;
  }

  return (
    <>
      <Seo title="Emotional Safety Audit" description="A private emotional safety self-check offered as a free Jonny's Towing community resource." noIndex />
      <section className="mental-hero">
        <div className="container mental-hero__content">
          <span className="eyebrow">Community outreach • Personal check-in</span>
          <Heart size={44} />
          <h1>Emotional Safety Audit</h1>
          <p>A quiet, private space to check in with how emotionally safe life has felt across home, work, relationships and community.</p>
          <div className="mental-actions"><button className="button button--ghost" onClick={() => window.print()}><Printer size={18} /> Print this page</button></div>
        </div>
      </section>
      <section className="section container mental-layout">
        <article className="mental-intro">
          <h2>Before you begin</h2>
          <p>This self-check is not a diagnosis. Your answers stay in this browser and are not saved by this page. You may choose to email only the summary after completing it.</p>
          <div className="crisis-box">
            <AlertTriangle />
            <div><strong>Need immediate support?</strong><p>Call 9-1-1 for immediate danger. In Canada, call or text 9-8-8 for suicide crisis support. In British Columbia, call 8-1-1 for health guidance.</p></div>
          </div>
        </article>
        <div className="audit-progress"><span>{answeredCount} of {totalQuestions} answered</span><div><i style={{ width: `${(answeredCount / totalQuestions) * 100}%` }} /></div></div>
        <form className="audit-form" onSubmit={(event) => event.preventDefault()}>
          {sections.map((section, sectionIndex) => (
            <fieldset key={section.title}>
              <legend>{section.title}</legend>
              {section.questions.map((question, questionIndex) => {
                const id = `${sectionIndex}-${questionIndex}`;
                return (
                  <div className="audit-question" key={id}>
                    <p>{question}</p>
                    <div className="rating-row" role="radiogroup" aria-label={question}>
                      {[1, 2, 3, 4, 5].map((value) => (
                        <label key={value} className={answers[id] === value ? 'selected' : ''}>
                          <input type="radio" name={id} value={value} checked={answers[id] === value} onChange={() => setAnswers((current) => ({ ...current, [id]: value }))} />
                          <span>{value}</span>
                        </label>
                      ))}
                    </div>
                    <small>1 = strongly disagree · 5 = strongly agree</small>
                  </div>
                );
              })}
            </fieldset>
          ))}
        </form>
        <section className={`audit-result ${complete ? 'audit-result--complete' : ''}`}>
          <span className="eyebrow">Your private snapshot</span>
          {complete ? <><strong>{score.toFixed(1)} / 5</strong><h2>{label}</h2><p>{summary}</p></> : <><h2>Complete all questions to calculate your result.</h2><p>Your responses are only held in memory while this page is open.</p></>}
          <button className="button button--primary" type="button" onClick={emailResults}><Mail size={18} /> Email my summary</button>
          <small>The wecare inbox is not a crisis or 24/7 emergency service.</small>
        </section>
      </section>
    </>
  );
}
